import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/outline'
import stravovani from '../img/card/modal/stravovani.jpg'
import rozvoz from '../img/card/modal/rozvoz.jpg'
import zdravi from '../img/card/modal/zdravi.jpg'
import dohled from '../img/card/modal/dohled.jpg'
import hygiena from '../img/card/modal/hygiena.jpg'
import doprava from '../img/card/modal/doprava.jpg'
import pomoc from '../img/card/modal/pomoc.jpg' 

const galleryImages = [
	{ id: 1, src: stravovani, alt: 'Pomoc při zvládání běžných úkonů péče o vlastní osobu' },
	{ id: 2, src: hygiena, alt: 'Pomoc při osobní hygieně' },
	{ id: 3, src: rozvoz, alt: 'Poskytnutí stravy nebo pomoc při zajištění stravy, dovoz obědů' },
	{ id: 4, src: pomoc, alt: 'Pomoc při zajištění chodu domácnosti' },
	{ id: 5, src: doprava, alt: 'Zprostředkování kontaktu se společenským prostředím' },
	{ id: 6, src: dohled, alt: 'Pomoc při uplatňování práv, oprávněných zájmů a při obstarávání osobních záležitostí' },
	{ id: 7, src: zdravi, alt: 'Dohled - pomoc při zajištění bezpečí a možnosti setrvání v přirozeném sociálním prostředí.' },
];

const variants = {
  initial: (direction) => {
	return {
		x: direction > 0 ? 500 : -500,
		opacity: 0,
	}
  },
  animate:  { 
	x: 0,
	opacity: 1,
	transition: { x: { type: 'spring', stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }
  },
  exit: (direction) => {
	return {
		x: direction > 0 ? -500 : 500,
		opacity: 0,
		transition: { x: { type: 'spring', stiffness: 300, damping: 30 }, opacity: { duration: 0.2 } }
	}
  },
};

const ModalGallery = ({
	open,
	onClose,
	initialCardId
}) => {

	// Stav pro aktuální index obrázku v galerii
	const [currentIndex, setCurrentIndex] = useState(0)
	const [direction, setDirection] = useState(0);

	// Nalezení indexu obrázku podle ID
	useEffect(() => {
		if (open && initialCardId != null) {
			const idx = galleryImages.findIndex(c => c.id === initialCardId)
			setCurrentIndex(idx >= 0 ? idx : 0)
		}
	}, [open, initialCardId])

	// Přidáme posluchač události pro zavření modálního okna pomocí klávesy Escape
	useEffect(() => {
		const onKey = (e) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [onClose]);

	// Zabráníme scrollování na pozadí, když je modal otevřený
	useEffect(() => {
		if (open) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => { document.body.style.overflow = ''; };
	}, [open]);

	if (!open) return null;

	const first = () => {
		setCurrentIndex(0)
		setDirection(-1)
	}

	const prev = () => {
		setCurrentIndex((i) => (i - 1 + galleryImages.length) % galleryImages.length)
		setDirection(-1)
	}
    
	const next = () => {
		setCurrentIndex((i) => (i + 1) % galleryImages.length)
		setDirection(1)
	}
    
	const last = () => {
		setCurrentIndex(galleryImages.length - 1)
		setDirection(1)
	}

  	const { src, alt } = galleryImages[currentIndex]

	//const image = galleryImages[currentIndex];
	

  	return createPortal(
		<AnimatePresence>
			<motion.div 
				role='dialog'
				aria-modal='true'
				aria-labelledby='modal-gallery-title'
				className='fixed inset-0 z-[999] bg-black/95 flex items-center justify-center' 
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
			>
				<div className='w-full max-w-3xl p-4 relative'>
					<h1 className='text-lg font-bold text-white mb-4 text-center'>{alt}</h1>
					
					<div className="relative w-full h-[70vh] overflow-hidden mb-4">
						<AnimatePresence initial={false} custom={direction}>
							<motion.img
								key={currentIndex}
								src={src}
								alt={alt}
								variants={variants}
								custom={direction}
								initial="initial"
								animate="animate"
								exit="exit"
								className="absolute inset-0 mx-auto my-auto object-contain rounded-lg"
							/>
						</AnimatePresence>
					</div>
						

					<div className="flex items-center justify-center space-x-4">
						<button 
							onClick={first} 
							aria-label="První" 
							className="px-3 py-1 bg-gray-300 hover:bg-amber-500 rounded  disabled:bg-gray-400 disabled:opacity-50 disabled:hover:bg-gray-400"
							disabled={currentIndex === 0}
						>
							<ChevronDoubleLeftIcon className="h-6 w-6" />
						</button>
						<button 
							onClick={prev} 
							aria-label="Předchozí" 
							className="px-3 py-1 bg-gray-300 hover:bg-amber-500 rounded  disabled:bg-gray-400 disabled:opacity-50 disabled:hover:bg-gray-400"
							disabled={currentIndex === 0}
						>
							<ChevronLeftIcon className="h-6 w-6" />
						</button>
						<span className='text-white'>{currentIndex + 1} / {galleryImages.length}</span>
						<button 
							onClick={next} 
							aria-label="Další" 
							className="px-3 py-1 bg-gray-300 hover:bg-amber-500 rounded  disabled:bg-gray-400 disabled:opacity-50 disabled:hover:bg-gray-400"
							disabled={currentIndex === galleryImages.length - 1}
						>
							<ChevronRightIcon className="h-6 w-6" />
						</button>
						<button 
							onClick={last} 
							aria-label="Poslední" 
							className={`px-3 py-1 bg-gray-300 hover:bg-amber-500 rounded disabled:bg-gray-400 disabled:opacity-50 disabled:hover:bg-gray-400`}
							disabled={currentIndex === galleryImages.length - 1}
						>
							<ChevronDoubleRightIcon className="h-6 w-6" />
						</button>
					</div>
					<div className="flex justify-center mt-4">
						<button 
							className='bg-red-600 text-white px-8 py-2 rounded-lg hover:bg-red-800 hover:shadow-md transition-colors duration-300'
							onClick={onClose}
							aria-label='Zavřít galerii'
						>
							Zavřít
						</button>	
					</div>
				</div>
				
					
				
			</motion.div>
		</AnimatePresence>,
		document.body
	)
}

export default ModalGallery
